import { useEffect, useState, useCallback } from "react";
import {
  getDistrictManagers,
  createDistrictManager,
  updateDistrictManager,
  deleteDistrictManager,
  getTierMultipliers,
  updateTierMultipliers,
  getDistrictManagerCommissions,
} from "../services/districtManagersApi";

const TABS = ["District Managers", "Tier Multipliers", "Commission Report"];
const TAB_KEY = "dm_active_tab";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const emptyManager = {
  districtManagerId: "",
  name: "",
  commissionRate: "",
  commissionType: "tiered",
  stores: "",
  createdAt: "",
  updatedAt: "",
};

const emptyTier = { name: "", multiplier: "" };

const getAllStoreIds = () => {
  const stores = [];
  for (let i = 1; i <= 23; i++) {
    if (i !== 20) stores.push(i);
  }
  return stores;
};

export default function DistrictManagers() {
  const [activeTab, setActiveTab] = useState(() => {
    const saved = sessionStorage.getItem(TAB_KEY);
    return saved !== null ? Number(saved) : 0;
  });

  // ── District Manager state ──────────────────────────────────────────────────
  const [managers, setManagers] = useState([]);
  const [form, setForm] = useState(emptyManager);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectAllStores, setSelectAllStores] = useState(false);

  // ── Tier Multiplier state ───────────────────────────────────────────────────
  const [tierMultipliers, setTierMultipliers] = useState([emptyTier]);
  const [tierLoading, setTierLoading] = useState(false);

  // ── Commission Report state ─────────────────────────────────────────────────
  const currentYear = new Date().getFullYear();
  const [commissionPeriodType, setCommissionPeriodType] = useState("month");
  const [commissionYear, setCommissionYear] = useState(currentYear);
  const [commissionMonth, setCommissionMonth] = useState(
    new Date().getMonth() + 1,
  );
  const [commissionQuarter, setCommissionQuarter] = useState("q1");
  const [commissionData, setCommissionData] = useState(null);
  const [commissionLoading, setCommissionLoading] = useState(false);

  // ── Loaders ─────────────────────────────────────────────────────────────────
  const loadManagers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getDistrictManagers();
      setManagers(Array.isArray(data) ? data : data?.data || []);
    } catch (err) {
      alert("Failed to load district managers: " + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTierMultipliers = useCallback(async () => {
    setTierLoading(true);
    try {
      const tiers = await getTierMultipliers();
      const tiersArray = Array.isArray(tiers) ? tiers : tiers?.data || [];
      if (!tiersArray || tiersArray.length === 0) {
        setTierMultipliers([emptyTier]);
      } else {
        setTierMultipliers(
          tiersArray.map(({ name, multiplier }) => ({
            name: name || "",
            multiplier: multiplier !== undefined ? multiplier.toString() : "",
          })),
        );
      }
    } catch (err) {
      alert("Failed to load tier multipliers: " + err.message);
    } finally {
      setTierLoading(false);
    }
  }, []);

  // ── Commission report fetch ─────────────────────────────────────────────────
  const handleFetchCommission = useCallback(async () => {
    setCommissionLoading(true);
    setCommissionData(null);
    try {
      const data = await getDistrictManagerCommissions({
        year: commissionYear,
        periodType: commissionPeriodType,
        month: commissionMonth,
        quarter: commissionQuarter,
      });
      setCommissionData(data);
    } catch (err) {
      alert("Error fetching commission report: " + err.message);
    } finally {
      setCommissionLoading(false);
    }
  }, [
    commissionYear,
    commissionPeriodType,
    commissionMonth,
    commissionQuarter,
  ]);

  // ── Init ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    loadManagers();
    loadTierMultipliers();
  }, [loadManagers, loadTierMultipliers]);

  useEffect(() => {
    sessionStorage.setItem(TAB_KEY, String(activeTab));
  }, [activeTab]);

  // ── Manager form handlers ───────────────────────────────────────────────────
  function handleChange(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function handleSelectAllStores(checked) {
    setSelectAllStores(checked);
    setForm((prev) => ({
      ...prev,
      stores: checked ? getAllStoreIds().join(", ") : "",
    }));
  }

  useEffect(() => {
    const currentStores = form.stores
      .split(",")
      .map((s) => Number(s.trim()))
      .filter((s) => !isNaN(s) && s > 0)
      .sort((a, b) => a - b);

    const allStores = getAllStoreIds().sort((a, b) => a - b);

    setSelectAllStores(
      currentStores.length === allStores.length &&
        currentStores.every((s, i) => s === allStores[i]),
    );
  }, [form.stores]);

  async function handleSubmit(e) {
    e.preventDefault();

    if (!form.name.trim()) {
      alert("Name is required");
      return;
    }
    if (!form.districtManagerId || isNaN(Number(form.districtManagerId))) {
      alert("Valid District Manager ID is required");
      return;
    }
    const commissionRate = parseFloat(form.commissionRate);
    if (isNaN(commissionRate) || commissionRate < 0 || commissionRate > 100) {
      alert("Commission Rate must be a number between 0 and 100");
      return;
    }

    const payload = {
      districtManagerId: Number(form.districtManagerId),
      name: form.name.trim(),
      commissionRate,
      commissionType: form.commissionType,
      stores: form.stores
        .split(",")
        .map((s) => Number(s.trim()))
        .filter((s) => !isNaN(s) && s > 0),
    };

    try {
      if (editingId !== null) {
        await updateDistrictManager(editingId, payload);
      } else {
        await createDistrictManager(payload);
      }
      setForm(emptyManager);
      setEditingId(null);
      setModalOpen(false);
      setSelectAllStores(false);
      await loadManagers();
    } catch (err) {
      alert("Error saving district manager: " + err.message);
    }
  }

  function handleEdit(manager) {
    setForm({
      districtManagerId: manager.districtManagerId,
      name: manager.name,
      commissionRate: manager.commissionRate.toString(),
      commissionType: manager.commissionType || "tiered",
      stores: Array.isArray(manager.stores) ? manager.stores.join(", ") : "",
      createdAt: manager.createdAt ?? "",
      updatedAt: manager.updatedAt ?? "",
    });
    setEditingId(manager.districtManagerId);
    setModalOpen(true);
  }

  function handleCancel() {
    setForm(emptyManager);
    setEditingId(null);
    setModalOpen(false);
    setSelectAllStores(false);
  }

  async function handleDelete(id) {
    if (!window.confirm(`Delete District Manager ${id}?`)) return;
    try {
      await deleteDistrictManager(id);
      await loadManagers();
    } catch (err) {
      alert("Error deleting district manager: " + err.message);
    }
  }

  // ── Tier multiplier handlers ────────────────────────────────────────────────
  function handleTierChange(index, field, value) {
    setTierMultipliers((prev) => {
      const newTiers = [...prev];
      newTiers[index] = { ...newTiers[index], [field]: value };
      return newTiers;
    });
  }

  function addTier() {
    setTierMultipliers((prev) => [...prev, { ...emptyTier }]);
  }

  function removeTier(index) {
    setTierMultipliers((prev) => {
      const newTiers = [...prev];
      newTiers.splice(index, 1);
      return newTiers.length > 0 ? newTiers : [{ ...emptyTier }];
    });
  }

  function parseTierMultipliers(tiers) {
    return tiers
      .filter(({ name, multiplier }) => name.trim() !== "" && multiplier !== "")
      .map(({ name, multiplier }) => ({
        name: name.trim(),
        multiplier: parseFloat(multiplier),
      }));
  }

  async function handleTierSubmit(e) {
    e.preventDefault();

    for (const tier of tierMultipliers) {
      if (tier.name.trim() === "") {
        alert("All tier multiplier names are required");
        return;
      }
      if (tier.multiplier === "" || isNaN(parseFloat(tier.multiplier))) {
        alert("All tier multipliers must be valid numbers");
        return;
      }
    }

    try {
      await updateTierMultipliers(parseTierMultipliers(tierMultipliers));
      alert("Tier multipliers updated successfully");
    } catch (err) {
      alert("Error updating tier multipliers: " + err.message);
    }
  }

  // ── Helpers ─────────────────────────────────────────────────────────────────
  function getPeriodLabel() {
    if (commissionPeriodType === "month") {
      return `${MONTH_NAMES[commissionMonth - 1]} ${commissionYear}`;
    }
    if (commissionPeriodType === "quarter") {
      return `${commissionQuarter.toUpperCase()} ${commissionYear}`;
    }
    return `YTD ${commissionYear} (Jan – present)`;
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-slate-900 mb-1">
            District Managers
          </h1>
          <p className="text-slate-600">
            Manage district managers, commission rates, and tier multipliers.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 mb-6">
          {TABS.map((tab, i) => (
            <button
              key={tab}
              onClick={() => setActiveTab(i)}
              className={`px-5 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === i
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* ── Tab 0: District Managers ── */}
        {activeTab === 0 && (
          <>
            <div className="mb-4 flex justify-end">
              <button
                onClick={() => {
                  setForm(emptyManager);
                  setEditingId(null);
                  setModalOpen(true);
                }}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
              >
                + Add New Manager
              </button>
            </div>

            <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
              {loading ? (
                <div className="p-12 text-center">
                  <div className="inline-block w-8 h-8 border-4 border-slate-300 border-t-blue-600 rounded-full animate-spin" />
                  <p className="mt-3 text-slate-600">Loading managers...</p>
                </div>
              ) : managers.length === 0 ? (
                <div className="p-12 text-center text-slate-600 font-medium">
                  No district managers found. Add your first manager to get
                  started.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        {[
                          "ID",
                          "Name",
                          "Commission Type",
                          "Commission Rate",
                          "Stores",
                          "Created At",
                          "Updated At",
                          "Actions",
                        ].map((h) => (
                          <th
                            key={h}
                            className={`px-6 py-3 text-xs font-semibold text-slate-700 uppercase tracking-wider ${
                              h === "Actions" ? "text-right" : "text-left"
                            }`}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {managers.map((m) => (
                        <tr
                          key={m.districtManagerId}
                          className="hover:bg-slate-50 transition-colors"
                        >
                          <td className="px-6 py-4 font-mono text-sm">
                            {m.districtManagerId}
                          </td>
                          <td className="px-6 py-4 font-semibold text-slate-900">
                            {m.name}
                          </td>
                          <td className="px-6 py-4 capitalize">
                            {m.commissionType}
                          </td>
                          <td className="px-6 py-4">
                            {Number(m.commissionRate).toFixed(4)}
                          </td>
                          <td className="px-6 py-4">
                            {Array.isArray(m.stores) ? m.stores.join(", ") : ""}
                          </td>
                          <td className="px-6 py-4 text-xs text-slate-600">
                            {m.createdAt
                              ? new Date(m.createdAt).toLocaleString()
                              : "-"}
                          </td>
                          <td className="px-6 py-4 text-xs text-slate-600">
                            {m.updatedAt
                              ? new Date(m.updatedAt).toLocaleString()
                              : "-"}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <button
                              onClick={() => handleEdit(m)}
                              className="text-blue-600 hover:text-blue-800 font-medium text-sm mr-4 transition-colors"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(m.districtManagerId)}
                              className="text-red-600 hover:text-red-800 font-medium text-sm transition-colors"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* ── Tab 1: Tier Multipliers ── */}
        {activeTab === 1 && (
          <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              Global Tier Multipliers
            </h2>

            {tierLoading ? (
              <p className="text-center text-slate-600">Loading tiers...</p>
            ) : (
              <form onSubmit={handleTierSubmit} className="space-y-4">
                {tierMultipliers.map((tier, index) => (
                  <div key={index} className="flex space-x-2 items-center">
                    <input
                      type="text"
                      placeholder="Tier Name (e.g. 30%)"
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                      value={tier.name}
                      onChange={(e) =>
                        handleTierChange(index, "name", e.target.value)
                      }
                      required
                    />
                    <input
                      type="number"
                      step="0.01"
                      placeholder="Multiplier"
                      className="w-24 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                      value={tier.multiplier}
                      onChange={(e) =>
                        handleTierChange(index, "multiplier", e.target.value)
                      }
                      required
                      min={0}
                      max={10}
                    />
                    <button
                      type="button"
                      className="text-red-600 hover:text-red-800 font-semibold px-2"
                      onClick={() => removeTier(index)}
                      aria-label="Remove tier multiplier"
                    >
                      &times;
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addTier}
                  className="text-blue-600 hover:text-blue-800 font-semibold"
                >
                  + Add Tier Multiplier
                </button>

                <button
                  type="submit"
                  className="mt-4 w-full bg-gradient-to-r from-green-600 to-green-700 text-white py-3 rounded-lg hover:from-green-700 hover:to-green-800 font-medium shadow-md hover:shadow-lg transition-all duration-200"
                >
                  Save Tier Multipliers
                </button>
              </form>
            )}
          </div>
        )}

        {/* ── Tab 2: Commission Report ── */}
        {activeTab === 2 && (
          <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6 space-y-6">
            <h2 className="text-2xl font-semibold text-slate-900">
              Commission Report
            </h2>

            <div className="flex flex-wrap gap-4 items-end">
              {/* Year */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Year
                </label>
                <input
                  type="number"
                  min={2000}
                  max={currentYear}
                  className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none w-28"
                  value={commissionYear}
                  onChange={(e) => {
                    setCommissionYear(Number(e.target.value));
                    setCommissionData(null);
                  }}
                />
              </div>

              {/* Period type */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Period Type
                </label>
                <select
                  className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  value={commissionPeriodType}
                  onChange={(e) => {
                    setCommissionPeriodType(e.target.value);
                    setCommissionData(null);
                  }}
                >
                  <option value="month">Monthly</option>
                  <option value="quarter">Quarterly</option>
                  <option value="ytd">Year to Date</option>
                </select>
              </div>

              {/* Month — only when period is "month" */}
              {commissionPeriodType === "month" && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Month
                  </label>
                  <select
                    className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    value={commissionMonth}
                    onChange={(e) => {
                      setCommissionMonth(Number(e.target.value));
                      setCommissionData(null);
                    }}
                  >
                    {MONTH_NAMES.map((name, i) => (
                      <option key={i + 1} value={i + 1}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Quarter — only when period is "quarter" */}
              {commissionPeriodType === "quarter" && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Quarter
                  </label>
                  <select
                    className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    value={commissionQuarter}
                    onChange={(e) => {
                      setCommissionQuarter(e.target.value);
                      setCommissionData(null);
                    }}
                  >
                    {["q1", "q2", "q3", "q4"].map((q) => (
                      <option key={q} value={q}>
                        {q.toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <button
                onClick={handleFetchCommission}
                disabled={commissionLoading}
                className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {commissionLoading ? "Loading…" : "Generate Report"}
              </button>
            </div>

            {commissionLoading && (
              <div className="text-center py-8">
                <div className="inline-block w-8 h-8 border-4 border-slate-300 border-t-blue-600 rounded-full animate-spin" />
                <p className="mt-3 text-slate-600">Fetching report…</p>
              </div>
            )}

            {commissionData && !commissionLoading && (
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-2">
                  Results — {getPeriodLabel()}
                </h3>
                <pre className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm overflow-x-auto text-slate-800">
                  {JSON.stringify(commissionData, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}

        {/* ── Add / Edit Modal ── */}
        {modalOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
            onClick={handleCancel}
          >
            <div
              className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-slate-800">
                  {editingId !== null
                    ? `Edit Manager ID: ${editingId}`
                    : "Add New District Manager"}
                </h2>
                <button
                  onClick={handleCancel}
                  className="text-slate-600 hover:text-slate-900 text-xl font-medium"
                  aria-label="Close modal"
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    District Manager ID
                  </label>
                  <input
                    required
                    type="number"
                    min={1}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                    value={form.districtManagerId}
                    onChange={(e) =>
                      handleChange("districtManagerId", e.target.value)
                    }
                    disabled={editingId !== null}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Name
                  </label>
                  <input
                    required
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                    value={form.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Commission Type
                  </label>
                  <select
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                    value={form.commissionType}
                    onChange={(e) =>
                      handleChange("commissionType", e.target.value)
                    }
                  >
                    <option value="tiered">Tiered</option>
                    <option value="flat">Flat</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Commission Rate
                  </label>
                  <input
                    required
                    type="number"
                    step="0.0001"
                    min={0}
                    max={100}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                    value={form.commissionRate}
                    onChange={(e) =>
                      handleChange("commissionRate", e.target.value)
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Stores
                  </label>
                  <div className="mb-3 flex items-center">
                    <input
                      type="checkbox"
                      id="selectAllStores"
                      checked={selectAllStores}
                      onChange={(e) => handleSelectAllStores(e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    />
                    <label
                      htmlFor="selectAllStores"
                      className="ml-2 text-sm text-slate-700 cursor-pointer"
                    >
                      Select all stores (1–23, excluding 20)
                    </label>
                  </div>
                  <input
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                    value={form.stores}
                    onChange={(e) => handleChange("stores", e.target.value)}
                    placeholder="e.g., 1, 2, 3, 4"
                  />
                </div>

                {editingId !== null && (
                  <div className="text-xs text-slate-500 space-y-1">
                    <p>
                      <strong>Created At:</strong>{" "}
                      {form.createdAt
                        ? new Date(form.createdAt).toLocaleString()
                        : "N/A"}
                    </p>
                    <p>
                      <strong>Updated At:</strong>{" "}
                      {form.updatedAt
                        ? new Date(form.updatedAt).toLocaleString()
                        : "N/A"}
                    </p>
                  </div>
                )}

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 rounded-lg hover:from-blue-700 hover:to-blue-800 font-medium shadow-md hover:shadow-lg transition-all duration-200"
                >
                  {editingId !== null ? "Update Manager" : "Add Manager"}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
// import { useEffect, useState } from "react";
// import {
//   getDistrictManagers,
//   createDistrictManager,
//   updateDistrictManager,
//   deleteDistrictManager,
//   getTierMultipliers,
//   updateTierMultipliers,
//   getDistrictManagerCommissions,
// } from "../services/districtManagersApi";

// const TABS = ["District Managers", "Tier Multipliers"];
// const TAB_KEY = "dm_active_tab";

// const MONTH_NAMES = [
//   "January",
//   "February",
//   "March",
//   "April",
//   "May",
//   "June",
//   "July",
//   "August",
//   "September",
//   "October",
//   "November",
//   "December",
// ];

// const emptyManager = {
//   districtManagerId: "",
//   name: "",
//   commissionRate: "",
//   commissionType: "tiered",
//   stores: "",
//   createdAt: "",
//   updatedAt: "",
// };

// const emptyTier = { name: "", multiplier: "" };

// const getAllStoreIds = () => {
//   const stores = [];
//   for (let i = 1; i <= 23; i++) {
//     if (i !== 20) stores.push(i);
//   }
//   return stores;
// };

// export default function DistrictManagers() {
//   const [activeTab, setActiveTab] = useState(() => {
//     const saved = sessionStorage.getItem(TAB_KEY);
//     return saved !== null ? Number(saved) : 0;
//   });

//   // ── District Manager state ──────────────────────────────────────────────────
//   const [managers, setManagers] = useState([]);
//   const [form, setForm] = useState(emptyManager);
//   const [editingId, setEditingId] = useState(null);
//   const [loading, setLoading] = useState(false);
//   const [modalOpen, setModalOpen] = useState(false);
//   const [selectAllStores, setSelectAllStores] = useState(false);

//   // ── Tier Multiplier state ───────────────────────────────────────────────────
//   const [tierMultipliers, setTierMultipliers] = useState([emptyTier]);
//   const [tierLoading, setTierLoading] = useState(false);

//   // ── Commission Report state ─────────────────────────────────────────────────
//   const currentYear = new Date().getFullYear();
//   const [commissionPeriodType, setCommissionPeriodType] = useState("month");
//   const [commissionYear, setCommissionYear] = useState(currentYear);
//   const [commissionMonth, setCommissionMonth] = useState(
//     new Date().getMonth() + 1,
//   );
//   const [commissionQuarter, setCommissionQuarter] = useState("q1");
//   const [commissionData, setCommissionData] = useState(null);
//   const [commissionLoading, setCommissionLoading] = useState(false);

//   // ── Init ────────────────────────────────────────────────────────────────────
//   useEffect(() => {
//     loadManagers();
//     loadTierMultipliers();
//   }, []);

//   useEffect(() => {
//     sessionStorage.setItem(TAB_KEY, String(activeTab));
//   }, [activeTab]);

//   // ── Loaders ─────────────────────────────────────────────────────────────────
//   async function loadManagers() {
//     setLoading(true);
//     try {
//       const data = await getDistrictManagers();
//       setManagers(data);
//     } catch (err) {
//       alert("Failed to load district managers: " + err.message);
//     } finally {
//       setLoading(false);
//     }
//   }

//   async function loadTierMultipliers() {
//     setTierLoading(true);
//     try {
//       const tiers = await getTierMultipliers();
//       if (!tiers || tiers.length === 0) {
//         setTierMultipliers([emptyTier]);
//       } else {
//         setTierMultipliers(
//           tiers.map(({ name, multiplier }) => ({
//             name: name || "",
//             multiplier: multiplier !== undefined ? multiplier.toString() : "",
//           })),
//         );
//       }
//     } catch (err) {
//       alert("Failed to load tier multipliers: " + err.message);
//     } finally {
//       setTierLoading(false);
//     }
//   }

//   // ── Commission report fetch ─────────────────────────────────────────────────
//   async function handleFetchCommission() {
//     setCommissionLoading(true);
//     setCommissionData(null);
//     console.log("Initiating fetch for commission report with parameters:");
//     try {
//       const data = await getDistrictManagerCommissions({
//         year: commissionYear,
//         periodType: commissionPeriodType, // "month" | "quarter" | "ytd"
//         month: commissionMonth,
//         quarter: commissionQuarter,
//       });
//       console.log("Commission report data received:", data);
//       setCommissionData(data);
//     } catch (err) {
//       alert("Error fetching commission report: " + err.message);
//     } finally {
//       setCommissionLoading(false);
//     }
//   }

//   // ── Manager form handlers ───────────────────────────────────────────────────
//   function handleChange(field, value) {
//     setForm((prev) => ({ ...prev, [field]: value }));
//   }

//   function handleSelectAllStores(checked) {
//     setSelectAllStores(checked);
//     setForm((prev) => ({
//       ...prev,
//       stores: checked ? getAllStoreIds().join(", ") : "",
//     }));
//   }

//   useEffect(() => {
//     const currentStores = form.stores
//       .split(",")
//       .map((s) => Number(s.trim()))
//       .filter((s) => !isNaN(s) && s > 0)
//       .sort((a, b) => a - b);

//     const allStores = getAllStoreIds().sort((a, b) => a - b);

//     setSelectAllStores(
//       currentStores.length === allStores.length &&
//         currentStores.every((s, i) => s === allStores[i]),
//     );
//   }, [form.stores]);

//   async function handleSubmit(e) {
//     e.preventDefault();

//     if (!form.name.trim()) {
//       alert("Name is required");
//       return;
//     }
//     if (!form.districtManagerId || isNaN(Number(form.districtManagerId))) {
//       alert("Valid District Manager ID is required");
//       return;
//     }
//     const commissionRate = parseFloat(form.commissionRate);
//     if (isNaN(commissionRate) || commissionRate < 0 || commissionRate > 100) {
//       alert("Commission Rate must be a number between 0 and 100");
//       return;
//     }

//     const payload = {
//       districtManagerId: Number(form.districtManagerId),
//       name: form.name.trim(),
//       commissionRate,
//       commissionType: form.commissionType,
//       stores: form.stores
//         .split(",")
//         .map((s) => Number(s.trim()))
//         .filter((s) => !isNaN(s) && s > 0),
//     };

//     try {
//       if (editingId !== null) {
//         await updateDistrictManager(editingId, payload);
//       } else {
//         await createDistrictManager(payload);
//       }
//       setForm(emptyManager);
//       setEditingId(null);
//       setModalOpen(false);
//       setSelectAllStores(false);
//       await loadManagers();
//     } catch (err) {
//       alert("Error saving district manager: " + err.message);
//     }
//   }

//   function handleEdit(manager) {
//     setForm({
//       districtManagerId: manager.districtManagerId,
//       name: manager.name,
//       commissionRate: manager.commissionRate.toString(),
//       commissionType: manager.commissionType || "tiered",
//       stores: Array.isArray(manager.stores) ? manager.stores.join(", ") : "",
//       createdAt: manager.createdAt ?? "",
//       updatedAt: manager.updatedAt ?? "",
//     });
//     setEditingId(manager.districtManagerId);
//     setModalOpen(true);
//   }

//   function handleCancel() {
//     setForm(emptyManager);
//     setEditingId(null);
//     setModalOpen(false);
//     setSelectAllStores(false);
//   }

//   async function handleDelete(id) {
//     if (!window.confirm(`Delete District Manager ${id}?`)) return;
//     try {
//       await deleteDistrictManager(id);
//       await loadManagers();
//     } catch (err) {
//       alert("Error deleting district manager: " + err.message);
//     }
//   }

//   // ── Tier multiplier handlers ────────────────────────────────────────────────
//   function handleTierChange(index, field, value) {
//     setTierMultipliers((prev) => {
//       const newTiers = [...prev];
//       newTiers[index] = { ...newTiers[index], [field]: value };
//       return newTiers;
//     });
//   }

//   function addTier() {
//     setTierMultipliers((prev) => [...prev, { ...emptyTier }]);
//   }

//   function removeTier(index) {
//     setTierMultipliers((prev) => {
//       const newTiers = [...prev];
//       newTiers.splice(index, 1);
//       return newTiers.length > 0 ? newTiers : [{ ...emptyTier }];
//     });
//   }

//   function parseTierMultipliers(tiers) {
//     return tiers
//       .filter(({ name, multiplier }) => name.trim() !== "" && multiplier !== "")
//       .map(({ name, multiplier }) => ({
//         name: name.trim(),
//         multiplier: parseFloat(multiplier),
//       }));
//   }

//   async function handleTierSubmit(e) {
//     e.preventDefault();

//     for (const tier of tierMultipliers) {
//       if (tier.name.trim() === "") {
//         alert("All tier multiplier names are required");
//         return;
//       }
//       if (tier.multiplier === "" || isNaN(parseFloat(tier.multiplier))) {
//         alert("All tier multipliers must be valid numbers");
//         return;
//       }
//     }

//     try {
//       await updateTierMultipliers(parseTierMultipliers(tierMultipliers));
//       alert("Tier multipliers updated successfully");
//     } catch (err) {
//       alert("Error updating tier multipliers: " + err.message);
//     }
//   }

//   // ── Helpers ─────────────────────────────────────────────────────────────────
//   function getPeriodLabel() {
//     if (commissionPeriodType === "month") {
//       return `${MONTH_NAMES[commissionMonth - 1]} ${commissionYear}`;
//     }
//     if (commissionPeriodType === "quarter") {
//       return `${commissionQuarter.toUpperCase()} ${commissionYear}`;
//     }
//     return `YTD ${commissionYear} (Jan – present)`;
//   }

//   // ── Render ──────────────────────────────────────────────────────────────────
//   return (
//     <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
//       <div className="max-w-4xl mx-auto">
//         {/* Header */}
//         <div className="mb-6">
//           <h1 className="text-3xl font-bold text-slate-900 mb-1">
//             District Managers
//           </h1>
//           <p className="text-slate-600">
//             Manage district managers, commission rates, and tier multipliers.
//           </p>
//         </div>

//         {/* Tabs */}
//         <div className="flex border-b border-slate-200 mb-6">
//           {TABS.map((tab, i) => (
//             <button
//               key={tab}
//               onClick={() => setActiveTab(i)}
//               className={`px-5 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
//                 activeTab === i
//                   ? "border-blue-600 text-blue-600"
//                   : "border-transparent text-slate-500 hover:text-slate-800"
//               }`}
//             >
//               {tab}
//             </button>
//           ))}
//         </div>

//         {/* ── Tab 0: District Managers ── */}
//         {activeTab === 0 && (
//           <>
//             <div className="mb-4 flex justify-end">
//               <button
//                 onClick={() => {
//                   setForm(emptyManager);
//                   setEditingId(null);
//                   setModalOpen(true);
//                 }}
//                 className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
//               >
//                 + Add New Manager
//               </button>
//             </div>

//             <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
//               {loading ? (
//                 <div className="p-12 text-center">
//                   <div className="inline-block w-8 h-8 border-4 border-slate-300 border-t-blue-600 rounded-full animate-spin" />
//                   <p className="mt-3 text-slate-600">Loading managers...</p>
//                 </div>
//               ) : managers.length === 0 ? (
//                 <div className="p-12 text-center text-slate-600 font-medium">
//                   No district managers found. Add your first manager to get
//                   started.
//                 </div>
//               ) : (
//                 <div className="overflow-x-auto">
//                   <table className="w-full">
//                     <thead className="bg-slate-50 border-b border-slate-200">
//                       <tr>
//                         {[
//                           "ID",
//                           "Name",
//                           "Commission Type",
//                           "Commission Rate",
//                           "Stores",
//                           "Created At",
//                           "Updated At",
//                           "Actions",
//                         ].map((h) => (
//                           <th
//                             key={h}
//                             className={`px-6 py-3 text-xs font-semibold text-slate-700 uppercase tracking-wider ${
//                               h === "Actions" ? "text-right" : "text-left"
//                             }`}
//                           >
//                             {h}
//                           </th>
//                         ))}
//                       </tr>
//                     </thead>
//                     <tbody className="divide-y divide-slate-200">
//                       {managers.map((m) => (
//                         <tr
//                           key={m.districtManagerId}
//                           className="hover:bg-slate-50 transition-colors"
//                         >
//                           <td className="px-6 py-4 font-mono text-sm">
//                             {m.districtManagerId}
//                           </td>
//                           <td className="px-6 py-4 font-semibold text-slate-900">
//                             {m.name}
//                           </td>
//                           <td className="px-6 py-4 capitalize">
//                             {m.commissionType}
//                           </td>
//                           <td className="px-6 py-4">
//                             {Number(m.commissionRate).toFixed(4)}
//                           </td>
//                           <td className="px-6 py-4">
//                             {Array.isArray(m.stores) ? m.stores.join(", ") : ""}
//                           </td>
//                           <td className="px-6 py-4 text-xs text-slate-600">
//                             {m.createdAt
//                               ? new Date(m.createdAt).toLocaleString()
//                               : "-"}
//                           </td>
//                           <td className="px-6 py-4 text-xs text-slate-600">
//                             {m.updatedAt
//                               ? new Date(m.updatedAt).toLocaleString()
//                               : "-"}
//                           </td>
//                           <td className="px-6 py-4 text-right">
//                             <button
//                               onClick={() => handleEdit(m)}
//                               className="text-blue-600 hover:text-blue-800 font-medium text-sm mr-4 transition-colors"
//                             >
//                               Edit
//                             </button>
//                             <button
//                               onClick={() => handleDelete(m.districtManagerId)}
//                               className="text-red-600 hover:text-red-800 font-medium text-sm transition-colors"
//                             >
//                               Delete
//                             </button>
//                           </td>
//                         </tr>
//                       ))}
//                     </tbody>
//                   </table>
//                 </div>
//               )}
//             </div>
//           </>
//         )}

//         {/* ── Tab 1: Tier Multipliers ── */}
//         {activeTab === 1 && (
//           <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
//             <h2 className="text-2xl font-semibold text-slate-900 mb-4">
//               Global Tier Multipliers
//             </h2>

//             {tierLoading ? (
//               <p className="text-center text-slate-600">Loading tiers...</p>
//             ) : (
//               <form onSubmit={handleTierSubmit} className="space-y-4">
//                 {tierMultipliers.map((tier, index) => (
//                   <div key={index} className="flex space-x-2 items-center">
//                     <input
//                       type="text"
//                       placeholder="Tier Name (e.g. 30%)"
//                       className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
//                       value={tier.name}
//                       onChange={(e) =>
//                         handleTierChange(index, "name", e.target.value)
//                       }
//                       required
//                     />
//                     <input
//                       type="number"
//                       step="0.01"
//                       placeholder="Multiplier"
//                       className="w-24 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
//                       value={tier.multiplier}
//                       onChange={(e) =>
//                         handleTierChange(index, "multiplier", e.target.value)
//                       }
//                       required
//                       min={0}
//                       max={10}
//                     />
//                     <button
//                       type="button"
//                       className="text-red-600 hover:text-red-800 font-semibold px-2"
//                       onClick={() => removeTier(index)}
//                       aria-label="Remove tier multiplier"
//                     >
//                       &times;
//                     </button>
//                   </div>
//                 ))}

//                 <button
//                   type="button"
//                   onClick={addTier}
//                   className="text-blue-600 hover:text-blue-800 font-semibold"
//                 >
//                   + Add Tier Multiplier
//                 </button>

//                 <button
//                   type="submit"
//                   className="mt-4 w-full bg-gradient-to-r from-green-600 to-green-700 text-white py-3 rounded-lg hover:from-green-700 hover:to-green-800 font-medium shadow-md hover:shadow-lg transition-all duration-200"
//                 >
//                   Save Tier Multipliers
//                 </button>
//               </form>
//             )}
//           </div>
//         )}

//         {/* ── Tab 2: Commission Report ── */}
//         {activeTab === 2 && (
//           <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6 space-y-6">
//             <h2 className="text-2xl font-semibold text-slate-900">
//               Commission Report
//             </h2>

//             <div className="flex flex-wrap gap-4 items-end">
//               {/* Year */}
//               <div>
//                 <label className="block text-sm font-medium text-slate-700 mb-1">
//                   Year
//                 </label>
//                 <input
//                   type="number"
//                   min={2000}
//                   max={currentYear}
//                   className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none w-28"
//                   value={commissionYear}
//                   onChange={(e) => {
//                     setCommissionYear(Number(e.target.value));
//                     setCommissionData(null);
//                   }}
//                 />
//               </div>

//               {/* Period type */}
//               <div>
//                 <label className="block text-sm font-medium text-slate-700 mb-1">
//                   Period Type
//                 </label>
//                 <select
//                   className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
//                   value={commissionPeriodType}
//                   onChange={(e) => {
//                     setCommissionPeriodType(e.target.value);
//                     setCommissionData(null);
//                   }}
//                 >
//                   <option value="month">Monthly</option>
//                   <option value="quarter">Quarterly</option>
//                   <option value="ytd">Year to Date</option>
//                 </select>
//               </div>

//               {/* Month — only when period is "month" */}
//               {commissionPeriodType === "month" && (
//                 <div>
//                   <label className="block text-sm font-medium text-slate-700 mb-1">
//                     Month
//                   </label>
//                   <select
//                     className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
//                     value={commissionMonth}
//                     onChange={(e) => {
//                       setCommissionMonth(Number(e.target.value));
//                       setCommissionData(null);
//                     }}
//                   >
//                     {MONTH_NAMES.map((name, i) => (
//                       <option key={i + 1} value={i + 1}>
//                         {name}
//                       </option>
//                     ))}
//                   </select>
//                 </div>
//               )}

//               {/* Quarter — only when period is "quarter" */}
//               {commissionPeriodType === "quarter" && (
//                 <div>
//                   <label className="block text-sm font-medium text-slate-700 mb-1">
//                     Quarter
//                   </label>
//                   <select
//                     className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
//                     value={commissionQuarter}
//                     onChange={(e) => {
//                       setCommissionQuarter(e.target.value);
//                       setCommissionData(null);
//                     }}
//                   >
//                     {["q1", "q2", "q3", "q4"].map((q) => (
//                       <option key={q} value={q}>
//                         {q.toUpperCase()}
//                       </option>
//                     ))}
//                   </select>
//                 </div>
//               )}

//               <button
//                 onClick={handleFetchCommission}
//                 disabled={commissionLoading}
//                 className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
//               >
//                 {commissionLoading ? "Loading…" : "Generate Report"}
//               </button>
//             </div>

//             {commissionLoading && (
//               <div className="text-center py-8">
//                 <div className="inline-block w-8 h-8 border-4 border-slate-300 border-t-blue-600 rounded-full animate-spin" />
//                 <p className="mt-3 text-slate-600">Fetching report…</p>
//               </div>
//             )}

//             {commissionData && !commissionLoading && (
//               <div>
//                 <h3 className="text-sm font-semibold text-slate-700 mb-2">
//                   Results — {getPeriodLabel()}
//                 </h3>
//                 <pre className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm overflow-x-auto text-slate-800">
//                   {JSON.stringify(commissionData, null, 2)}
//                 </pre>
//               </div>
//             )}
//           </div>
//         )}

//         {/* ── Add / Edit Modal ── */}
//         {modalOpen && (
//           <div
//             className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
//             onClick={handleCancel}
//           >
//             <div
//               className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
//               onClick={(e) => e.stopPropagation()}
//               role="dialog"
//               aria-modal="true"
//             >
//               <div className="flex items-center justify-between mb-6">
//                 <h2 className="text-xl font-semibold text-slate-800">
//                   {editingId !== null
//                     ? `Edit Manager ID: ${editingId}`
//                     : "Add New District Manager"}
//                 </h2>
//                 <button
//                   onClick={handleCancel}
//                   className="text-slate-600 hover:text-slate-900 text-xl font-medium"
//                   aria-label="Close modal"
//                 >
//                   ×
//                 </button>
//               </div>

//               <form onSubmit={handleSubmit} className="space-y-5">
//                 <div>
//                   <label className="block text-sm font-medium text-slate-700 mb-2">
//                     District Manager ID
//                   </label>
//                   <input
//                     required
//                     type="number"
//                     min={1}
//                     className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
//                     value={form.districtManagerId}
//                     onChange={(e) =>
//                       handleChange("districtManagerId", e.target.value)
//                     }
//                     disabled={editingId !== null}
//                   />
//                 </div>

//                 <div>
//                   <label className="block text-sm font-medium text-slate-700 mb-2">
//                     Name
//                   </label>
//                   <input
//                     required
//                     className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
//                     value={form.name}
//                     onChange={(e) => handleChange("name", e.target.value)}
//                   />
//                 </div>

//                 <div>
//                   <label className="block text-sm font-medium text-slate-700 mb-2">
//                     Commission Type
//                   </label>
//                   <select
//                     className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
//                     value={form.commissionType}
//                     onChange={(e) =>
//                       handleChange("commissionType", e.target.value)
//                     }
//                   >
//                     <option value="tiered">Tiered</option>
//                     <option value="flat">Flat</option>
//                   </select>
//                 </div>

//                 <div>
//                   <label className="block text-sm font-medium text-slate-700 mb-2">
//                     Commission Rate
//                   </label>
//                   <input
//                     required
//                     type="number"
//                     step="0.0001"
//                     min={0}
//                     max={100}
//                     className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
//                     value={form.commissionRate}
//                     onChange={(e) =>
//                       handleChange("commissionRate", e.target.value)
//                     }
//                   />
//                 </div>

//                 <div>
//                   <label className="block text-sm font-medium text-slate-700 mb-2">
//                     Stores
//                   </label>
//                   <div className="mb-3 flex items-center">
//                     <input
//                       type="checkbox"
//                       id="selectAllStores"
//                       checked={selectAllStores}
//                       onChange={(e) => handleSelectAllStores(e.target.checked)}
//                       className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
//                     />
//                     <label
//                       htmlFor="selectAllStores"
//                       className="ml-2 text-sm text-slate-700 cursor-pointer"
//                     >
//                       Select all stores (1–23, excluding 20)
//                     </label>
//                   </div>
//                   <input
//                     className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
//                     value={form.stores}
//                     onChange={(e) => handleChange("stores", e.target.value)}
//                     placeholder="e.g., 1, 2, 3, 4"
//                   />
//                 </div>

//                 {editingId !== null && (
//                   <div className="text-xs text-slate-500 space-y-1">
//                     <p>
//                       <strong>Created At:</strong>{" "}
//                       {form.createdAt
//                         ? new Date(form.createdAt).toLocaleString()
//                         : "N/A"}
//                     </p>
//                     <p>
//                       <strong>Updated At:</strong>{" "}
//                       {form.updatedAt
//                         ? new Date(form.updatedAt).toLocaleString()
//                         : "N/A"}
//                     </p>
//                   </div>
//                 )}

//                 <button
//                   type="submit"
//                   className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 rounded-lg hover:from-blue-700 hover:to-blue-800 font-medium shadow-md hover:shadow-lg transition-all duration-200"
//                 >
//                   {editingId !== null ? "Update Manager" : "Add Manager"}
//                 </button>
//               </form>
//             </div>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// }
